"use client";

import { useEffect, useState, useRef } from "react";

interface NavbarState {
  isScrolled: boolean;
}

interface UseNavbarOptions {
  threshold?: number;
}

/**
 * Optimized navbar hook using IntersectionObserver for transparency detection.
 *
 * Perfect for sticky navbars that change background transparency based on scroll position.
 * The navbar remains always visible, only the background becomes transparent.
 */
export function useNavbar({ threshold = 0 }: UseNavbarOptions = {}): NavbarState & {
  heroRef: React.RefObject<HTMLElement | null>;
} {
  const [state, setState] = useState<NavbarState>({
    isScrolled: false,
  });

  const heroRef = useRef<HTMLElement>(null);

  useEffect(
    function setupTopSentinelObserver() {
      // Use a top sentinel so IO controls the state precisely; frost when sentinel leaves viewport.
      const sentinel = document.createElement("div");
      sentinel.setAttribute("aria-hidden", "true");
      sentinel.style.height = "1px";
      sentinel.style.width = "1px";
      sentinel.style.position = "absolute";
      sentinel.style.top = "0";
      sentinel.style.left = "0";
      document.body.prepend(sentinel);

      const observer = new IntersectionObserver(
        entries => {
          const [entry] = entries;
          const offTop = !entry.isIntersecting;
          if (threshold > 0) {
            setState({ isScrolled: offTop || window.scrollY > threshold });
          } else {
            setState({ isScrolled: offTop });
          }
        },
        { root: null, threshold: 0 }
      );

      observer.observe(sentinel);

      return () => {
        observer.disconnect();
        if (sentinel.parentNode) sentinel.parentNode.removeChild(sentinel);
      };
    },
    [threshold]
  );

  return { ...state, heroRef };
}
